// Dependencies
import axios from 'axios'

// Services
import { accessHeaders } from "../../lib/cookie"

// Utils
import {
  ClientUtility as Client
} from '../utils/client-utility'

import {
  ConvertUtility as Convert
} from '../utils/convert-utility'

/**
 * User detail properties
 * @typedef User
 * @type {Object}
 * @property {String} firstName - First name
 * @property {String} lastName  - Last name
 * @property {String} mobileNo  - Contact mobile number
 * @property {String} [email]   - Contact email
 */

/**
 * User image user record props
 * @typedef UserRecord
 * @type {Object}
 * @property {String} role - OWNER, RESIDENT, or RENTER
 * @typedef {User & UserRecord & HomeAddress} UserImport
 */

/**
 * User create parameters
 * @typedef UserCreate
 * @type {Object}
 * @property {String} homeId    - Non-SC home ID
 * @property {String} firstName - First name
 * @property {String} lastName  - Last name
 * @property {String} mobileNo  - Contact mobile number
 * @property {String} [email]   - Contact email
 * @property {String} role      - OWNER, RESIDENT, or RENTER
 */

/**
 * Creation result properties
 * @typedef CreateResult
 * @type {Object}
 * @property {String} id        - Unique ID
 * @property {String} createdAt - Creation time
 */

/**
 * User update parameters
 * @typedef UserUpdate
 * @type {Object}
 * @property {String} [firstName] - First name
 * @property {String} [lastName]  - Last name
 * @property {String} [mobileNo]  - Contact mobile number
 * @property {String} [email]     - Contact email
 * @property {String} [role]      - OWNER, RESIDENT, or RENTER
 */

/**
 * Updation result properties
 * @typedef UpdateResult
 * @type {Object}
 * @property {Boolean} updated - Update state
 */

/**
 * User filter parameters
 * @typedef UserFilter
 * @type {Object}
 * @property {String} [text]  - Text search
 * @property {String} page    - Page number
 * @property {String} limit   - Limit items
 */

/**
 * User role properties
 * @typedef UserRole
 * @type {Object}
 * @property {Number} id    - Role ID
 * @property {String} code  - OWNER, RESIDENT, or RENTER
 */

/**
 * User home properties
 * @typedef UserHome
 * @type {Object}
 * @property {Number} addressNo   - Address number
 * @property {String} addressRoad - Address road
 */

/**
 * User project properties
 * @typedef UserProject
 * @type {Object}
 * @property {String} id    - Project ID
 * @property {String} code  - Project code
 * @property {String} name  - Project name
 * @property {String} icon  - Project icon URL
 */

/**
 * User detail properties
 * @typedef UserDetail
 * @type {Object}
 * @property {Number} id            - User ID
 * @property {UserRole} role        - User Role
 * @property {UserHome} home        - Home detail
 * @property {UserProject} project  - Project detail
 * @typedef {User & UserDetail} UserItem
 */

/**
 * User list properties
 * @typedef UserList
 * @type {Object}
 * @property {UserItem[]} items - User list
 * @property {Number} total     - Total user
 */

/**
 * Deletion result properties
 * @typedef DeleteResult
 * @type {Object}
 * @property {Boolean} deleted - Delete state
 */

export class NonScUserClient {

  // Request Endpoint
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/user/nonsc`
  )

  // Import Fields
  static fields = [
    'firstName',
    'lastName',
    'mobileNo',
    'email',
    'role',
    'addressNo',
    'area',
    'structure',
    'addressRoad',
    'tumbonPrefix',
    'tumbonName',
    'amphurPrefix',
    'amphurName',
    'provinceName',
    'zipCode',
    'transferDate',
  ]

  /**
   * Fetch user details
   * @param {Number} userId - Non-SC User ID
   * @returns {Promise<>} User details
   */
  static async get(userId) {
    
    // Request URL
    const url = `${this.endpoint}/${userId}`
    
    // Default Result
    let result = null
    
    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Home List
      const { data } = await axios.get(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user.get', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Fetch user list
   * @param {UserFilter} params - Filter parameters
   * @returns {Promise<UserList>} User list 
   */
  static async list(params = {}) {
    
    // Request URL
    const url = `${this.endpoint  }/list`

    // Default Result
    let result = {
      total: 0, items: []
    }
    
    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Project List
      const { data } = await axios.get(
        url, { headers, params }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user.list', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request import users
   * @param {String} projectCode    - Project code 
   * @param {UserImport[]} users  - Import user list
   * @returns {Promise<*>} Import result
   */
  static async import(projectCode, items = []) {
    
    // Request URL
    const url = `${this.endpoint  }/import`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Create Project
      const { data } = await axios.post(
        url, { projectCode, items }, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user.import', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request create users
   * @param {UserCreate} payload - Create parameters
   * @returns {Promise<*>} Create result
   */
  static async create(payload) {
    
    // Request URL
    const url = this.endpoint
    
    // Default Result
    let result = null
    
    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Update Project
      const { data } = await axios.post(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user.create', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request update users
   * @param {Number} userId       - User ID
   * @param {UserUpdate} payload  - Update parameters
   * @returns {Promise<*>} Update result
   */
  static async update(userId, payload) {
    
    // Request URL
    const url = `${this.endpoint}/${userId}`
    
    // Default Result
    let result = null
    
    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Update Project
      const { data } = await axios.put(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user.update', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request remove user
   * @param {Number} userId - User ID
   * @returns {Promise<*>} Remove result 
   */
  static async remove(userId) {
        
    // Request URL
    const url = `${this.endpoint}/${userId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Remove Role
      const { data } = await axios.delete(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-user.remove', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }
}